import {
	dateInputToIsoEnd,
	dateInputToIsoStart,
} from '@/utils/auditLogDisplay';

export type SessionScopeRole = 'user' | 'professional';

export type SessionListSortField = 'created_at' | 'selected_date';

export type SessionListSortDirection = 'asc' | 'desc';

export const DEFAULT_SESSION_LIST_SORT: SessionListSortField = 'selected_date';

export const DEFAULT_SESSION_LIST_SORT_DIRECTION: SessionListSortDirection =
	'desc';

const UUID_RE =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = (value: string | null | undefined): value is string =>
	!!value?.trim() && UUID_RE.test(value.trim());

export const SESSION_ADMIN_SEARCH_PLACEHOLDER =
	'Nome ou e-mail do profissional ou participante';

export const SESSION_ADMIN_SEARCH_HINT =
	'Busca por trecho no índice da sessão (nomes e e-mails indexados). Não inclui telefone, CPF ou ID.';

export interface SessionAdminUrlFilters {
	search: string;
	status: Sessions.SessionStatus | null;
	type: Sessions.SessionType | null;
	payment: Sessions.PaymentStatus | null;
	/** Ausente na URL = default backend (`selected_date`) */
	sort: SessionListSortField | null;
	/** Ausente na URL = default backend (`desc`) */
	sortDirection: SessionListSortDirection | null;
	dateFrom: string;
	dateTo: string;
	role: SessionScopeRole | null;
	targetId: string | null;
	patientId: string | null;
}

export interface SessionAdminApiParams extends Sessions.SessionParams {
	sort?: SessionListSortField;
	sort_direction?: SessionListSortDirection;
	targetID?: string;
}

const SESSION_STATUSES: Sessions.SessionStatus[] = [
	'CREATED',
	'CONFIRMED',
	'STARTED',
	'FINISHED',
	'CANCELLED',
];

const SESSION_TYPES: Sessions.SessionType[] = [
	'DEFAULT',
	'RETURN',
	'GROUP',
	'P2P',
	'P2P_TRANSCRIPTION_ONLY',
];

const PAYMENT_STATUSES: Sessions.PaymentStatus[] = [
	'WAITING',
	'DONE',
	'FAILED',
	'REFUNDED',
	'EXPIRED',
];

const SORT_FIELDS: SessionListSortField[] = ['selected_date', 'created_at'];

const SORT_DIRECTIONS: SessionListSortDirection[] = ['asc', 'desc'];

const SCOPE_ROLES: SessionScopeRole[] = ['user', 'professional'];

export const SESSION_ADMIN_STATUS_OPTIONS: Array<{
	value: Sessions.SessionStatus;
	label: string;
}> = [
	{ value: 'CREATED', label: 'Aguardando pagamento' },
	{ value: 'CONFIRMED', label: 'Confirmada' },
	{ value: 'STARTED', label: 'Iniciada' },
	{ value: 'FINISHED', label: 'Finalizada' },
	{ value: 'CANCELLED', label: 'Cancelada' },
];

export const SESSION_ADMIN_TYPE_OPTIONS: Array<{
	value: Sessions.SessionType;
	label: string;
}> = [
	{ value: 'DEFAULT', label: 'Individual' },
	{ value: 'P2P', label: 'P2P (profissional)' },
	{ value: 'P2P_TRANSCRIPTION_ONLY', label: 'P2P transcrição' },
	{ value: 'RETURN', label: 'Retorno' },
	{ value: 'GROUP', label: 'Em grupo' },
];

export const SESSION_ADMIN_PAYMENT_OPTIONS: Array<{
	value: Sessions.PaymentStatus;
	label: string;
}> = [
	{ value: 'WAITING', label: 'Aguardando' },
	{ value: 'DONE', label: 'Pago' },
	{ value: 'FAILED', label: 'Falhou' },
	{ value: 'REFUNDED', label: 'Reembolsado' },
	{ value: 'EXPIRED', label: 'Expirado' },
];

export const SESSION_ADMIN_SORT_OPTIONS: Array<{
	value: SessionListSortField;
	label: string;
}> = [
	{ value: 'selected_date', label: 'Data agendada' },
	{ value: 'created_at', label: 'Data de criação' },
];

export const SESSION_ADMIN_SORT_DIRECTION_OPTIONS: Array<{
	value: SessionListSortDirection;
	label: string;
}> = [
	{ value: 'desc', label: 'Mais recentes primeiro' },
	{ value: 'asc', label: 'Mais antigas primeiro' },
];

export const SESSION_ADMIN_SCOPE_ROLE_OPTIONS: Array<{
	value: SessionScopeRole;
	label: string;
}> = [
	{ value: 'professional', label: 'Profissional' },
	{ value: 'user', label: 'Usuário (paciente)' },
];

const parseEnum = <T extends string>(
	value: string | null,
	allowed: readonly T[],
): T | null => {
	if (!value) return null;
	return allowed.includes(value as T) ? (value as T) : null;
};

export const parseSessionAdminUrlFilters = (
	searchParams: URLSearchParams,
): SessionAdminUrlFilters => {
	const legacyGroup = searchParams.get('group');
	const legacyType =
		legacyGroup === 'GROUP' || legacyGroup === 'DEFAULT'
			? (legacyGroup as Sessions.SessionType)
			: null;

	const roleRaw = searchParams.get('role');
	const role = parseEnum(roleRaw, SCOPE_ROLES);
	const targetIdRaw = searchParams.get('targetID')?.trim() ?? null;
	const targetId = isUuid(targetIdRaw) ? targetIdRaw : null;
	const patientIdRaw = searchParams.get('patient_id')?.trim() ?? null;
	const patientId = isUuid(patientIdRaw) ? patientIdRaw : null;

	return {
		search: searchParams.get('search')?.trim() ?? '',
		status: parseEnum(searchParams.get('status'), SESSION_STATUSES),
		type: parseEnum(searchParams.get('type'), SESSION_TYPES) ?? legacyType,
		payment: parseEnum(searchParams.get('payment'), PAYMENT_STATUSES),
		sort: parseEnum(searchParams.get('sort'), SORT_FIELDS),
		sortDirection: parseEnum(
			searchParams.get('sort_direction'),
			SORT_DIRECTIONS,
		),
		dateFrom: searchParams.get('date_from')?.trim() ?? '',
		dateTo: searchParams.get('date_to')?.trim() ?? '',
		role: role && targetId ? role : null,
		targetId: role && targetId ? targetId : null,
		patientId:
			role === 'professional' && targetId && patientId ? patientId : null,
	};
};

export const hasSessionScope = (filters: SessionAdminUrlFilters): boolean =>
	filters.role != null && filters.targetId != null;

export const buildSessionAdminApiParams = (
	filters: SessionAdminUrlFilters,
	pagination: { page: number; limit: number },
): SessionAdminApiParams => {
	const params: SessionAdminApiParams = {
		page: pagination.page,
		limit: pagination.limit,
	};

	const sortField = filters.sort ?? DEFAULT_SESSION_LIST_SORT;
	const sortDirection =
		filters.sortDirection ?? DEFAULT_SESSION_LIST_SORT_DIRECTION;

	if (sortField !== DEFAULT_SESSION_LIST_SORT) {
		params.sort = sortField;
	}
	if (sortDirection !== DEFAULT_SESSION_LIST_SORT_DIRECTION) {
		params.sort_direction = sortDirection;
	}

	if (filters.search) {
		params.search = filters.search.toLowerCase();
	}
	if (filters.status) params.status = filters.status;
	if (filters.payment) params.payment = filters.payment;

	const scoped = hasSessionScope(filters);
	if (!scoped && filters.type) {
		params.type = filters.type;
	}

	if (scoped && filters.role && filters.targetId) {
		params.role = filters.role;
		params.targetID = filters.targetId;
		if (filters.role === 'professional' && filters.patientId) {
			params.patient_id = filters.patientId;
		}
	}

	if (filters.dateFrom && filters.dateTo) {
		params.date_a = dateInputToIsoStart(filters.dateFrom);
		params.date_b = dateInputToIsoEnd(filters.dateTo);
	}

	return params;
};

export const sessionAdminFiltersQueryKey = (
	filters: SessionAdminUrlFilters,
	pagination: { page: number; limit: number },
): unknown[] => [
	'sessions',
	false,
	filters.search,
	filters.status,
	filters.type,
	filters.payment,
	filters.sort ?? DEFAULT_SESSION_LIST_SORT,
	filters.sortDirection ?? DEFAULT_SESSION_LIST_SORT_DIRECTION,
	filters.dateFrom,
	filters.dateTo,
	filters.role,
	filters.targetId,
	filters.patientId,
	pagination.page,
	pagination.limit,
];
